import React, { useEffect, useState, useCallback, useRef } from "react";
import { authFetch } from "../api/api";

import { useMsal } from "@azure/msal-react";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

/* =========================
   FILE TYPE RESTRICTIONS
========================= */

const ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc", "docx",
  "xls", "xlsx", "csv",
  "jpg", "jpeg", "png", "tif", "tiff",
  "txt",
]);

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/tiff",
]);

/*
  Comma-separated list for the <input accept> attribute.
  Guides the browser file picker — not a security enforcement.
*/
const ACCEPT_STRING =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.tif,.tiff,.txt";

/*
  Returns an array of error strings for a file.
  Empty array = file is valid.
  Extension is the primary gate. MIME type is cross-checked when available
  (skipped if empty or generic "application/octet-stream" — some OS/browsers
  report this for valid files like .csv).
*/
function getFileErrors(file) {
  const errors = [];

  const ext = file.name.split(".").pop().toLowerCase();
  const mime = file.type;

  const extValid = ALLOWED_EXTENSIONS.has(ext);
  const mimeValid =
    !mime ||
    mime === "application/octet-stream" ||
    ALLOWED_MIME_TYPES.has(mime);

  if (!extValid || !mimeValid) {
    errors.push(`Unsupported type (.${ext})`);
  }

  if (file.size > MAX_FILE_SIZE) {
    errors.push(`Exceeds 50MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
  }

  return errors;
}

export default function ManualUpload() {
  const { instance, accounts } = useMsal();

  const [documentTypes, setDocumentTypes] = useState([]);

  const [lobOptions, setLobOptions] = useState([]);

  /* =========================
     FORM
  ========================= */

  const [files, setFiles] = useState([]);

  const [documentTypeId, setDocumentTypeId] = useState("");

  const [policyNumber, setPolicyNumber] = useState("");

  const [comments, setComments] = useState("");

  const [lob, setLob] = useState("");

  const [agencyNo, setAgencyNo] = useState("");

  const [primaryInsured, setPrimaryInsured] = useState("");
  const uploadControllerRef = useRef(null);

  /* =========================
     UI
  ========================= */

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState(null);

  const [hoveredChipIndex, setHoveredChipIndex] = useState(null);

  const [isTablet, setIsTablet] = useState(
    () => window.matchMedia("(max-width: 767px)").matches
  );
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 567px)").matches
  );

  useEffect(() => {
    const mqTablet = window.matchMedia("(max-width: 767px)");
    const mqMobile = window.matchMedia("(max-width: 567px)");
    const handleTablet = (e) => setIsTablet(e.matches);
    const handleMobile = (e) => setIsMobile(e.matches);
    mqTablet.addEventListener("change", handleTablet);
    mqMobile.addEventListener("change", handleMobile);
    return () => {
      mqTablet.removeEventListener("change", handleTablet);
      mqMobile.removeEventListener("change", handleMobile);
    };
  }, []);

  /* =========================
     LOAD DOCUMENT TYPES
  ========================= */

  const loadLookups = useCallback(
    async (signal) => {
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

        /*
        PARALLEL LOOKUPS
      */

        const [docResponse, lobResponse] = await Promise.all([
          authFetch(
            instance,
            accounts,
            `${baseUrl}/api/admin/lookup/document_type`,
            {
              signal,
            },
          ),

          authFetch(
            instance,
            accounts,
            `${baseUrl}/api/admin/lookup/line_of_business`,
            {
              signal,
            },
          ),
        ]);

        if (!docResponse.ok || !lobResponse.ok) {
          throw new Error("Failed to load lookups");
        }

        const [docResult, lobResult] = await Promise.all([
          docResponse.json(),
          lobResponse.json(),
        ]);

        /*
        Ignore cancelled requests
      */

        if (signal?.aborted) {
          return;
        }

        const docValues = docResult?.data || [];

        const lobValues = lobResult?.data || [];

        setDocumentTypes(docValues);

        setLobOptions(lobValues);
      } catch (err) {
        /*
        Ignore cancellation
      */

        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Failed to load lookups", err);
      }
    },
    [instance, accounts],
  );

  useEffect(() => {
    const controller = new AbortController();

    loadLookups(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadLookups]);

  /* =========================
     SUBMIT
  ========================= */

  const handleUpload = async () => {
    /*
    Prevent duplicate uploads
  */

    if (loading) {
      return;
    }

    /*
    Cancel previous upload if any
  */

    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
    }

    const controller = new AbortController();

    uploadControllerRef.current = controller;

    try {
      setError("");

      setSuccess(null);

      /*
      VALIDATION
    */

      if (files.length === 0) {
        setError("Please select at least one file");

        return;
      }

      if (!documentTypeId) {
        setError("Document Type is required");

        return;
      }

      if (!policyNumber.trim()) {
        setError("Policy Number is required");

        return;
      }

      /*
      FILE VALIDATION SAFETY NET — Upload button is already disabled in the UI
      when invalid files are present. These checks are only reachable if the
      button is triggered programmatically.
    */

      const invalidType = files.find(
        (f) => !ALLOWED_EXTENSIONS.has(f.name.split(".").pop().toLowerCase()),
      );

      if (invalidType) {
        setError(
          `"${invalidType.name}" is not a supported file type. Remove it before uploading.`,
        );

        return;
      }

      const oversized = files.find((f) => f.size > MAX_FILE_SIZE);

      if (oversized) {
        setError(
          `"${oversized.name}" exceeds the 50MB size limit. Remove it before uploading.`,
        );

        return;
      }

      setLoading(true);

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      /*
      UPLOAD — Sequential approach (current backend accepts one file per request).
      Each file is uploaded individually with the same metadata.
      The loop stops immediately if any upload fails or is cancelled.

      ─────────────────────────────────────────────────────────────
      FUTURE: When the backend is updated to accept multiple files
      in a single request, replace the loop below with:

        const formData = new FormData();
        files.forEach((f) => formData.append("files[]", f));
        formData.append("document_type_id", documentTypeId);
        formData.append("policy_number", policyNumber.trim());
        formData.append("comments", comments);
        formData.append("lob", lob);
        formData.append("agency_no", agencyNo);
        formData.append("primary_insured", primaryInsured);

        const response = await authFetch(
          instance,
          accounts,
          `${baseUrl}/api/manual-upload/upload`,
          { method: "POST", signal: controller.signal, body: formData },
        );
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result?.message || "Upload failed");
        const uploadedFileNames = files.map((f) => f.name);

      ─────────────────────────────────────────────────────────────
    */

      const uploadedFileNames = [];

      for (const f of files) {
        if (controller.signal.aborted) {
          return;
        }

        const formData = new FormData();

        formData.append("file", f);

        formData.append("document_type_id", documentTypeId);

        formData.append("policy_number", policyNumber.trim());

        formData.append("comments", comments);

        formData.append("lob", lob);

        formData.append("agency_no", agencyNo);

        formData.append("primary_insured", primaryInsured);

        const response = await authFetch(
          instance,
          accounts,
          `${baseUrl}/api/manual-upload/upload`,
          {
            method: "POST",

            signal: controller.signal,

            body: formData,
          },
        );

        let result = {};

        try {
          result = await response.json();
        } catch {
          result = {};
        }

        if (!response.ok) {
          throw new Error(
            `"${f.name}" — ${result?.message || "Upload failed"}`,
          );
        }

        uploadedFileNames.push(f.name);
      }

      /*
      Ignore cancelled upload
    */

      if (controller.signal.aborted) {
        return;
      }

      setSuccess({
        uploadedFileNames,
      });

      /*
      RESET
    */

      setFiles([]);

      setPolicyNumber("");

      setComments("");

      setLob("");

      setAgencyNo("");

      setPrimaryInsured("");

      setDocumentTypeId("");

      const input = document.getElementById("manual-upload-input");

      if (input) {
        input.value = "";
      }
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error(err);

      setError(err.message || "Upload failed");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (uploadControllerRef.current) {
        uploadControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const fileCount = success.uploadedFileNames?.length || 1;
    const dismissMs = Math.min(
      8000,
      5000 + Math.max(0, fileCount - 1) * 2000,
    );

    const timer = setTimeout(() => {
      setSuccess(null);
    }, dismissMs);

    return () => clearTimeout(timer);
  }, [success]);
  /* =========================
     UI
  ========================= */

  const fieldInputStyle = isMobile
    ? { ...styles.input, ...styles.inputMobile }
    : styles.input;

  const fieldTextareaStyle = isMobile
    ? { ...styles.textarea, ...styles.textareaMobile }
    : styles.textarea;

  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, ...(isMobile ? styles.cardMobile : null) }}>
        {/* HEADER */}

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Manual Upload</h2>

            <p style={styles.sub}>
              Upload and classify documents with business metadata.
            </p>
          </div>
        </div>

        {/* FORM */}

        <div style={{ ...styles.formCard, ...(isMobile ? styles.formCardMobile : null) }}>
          {loading && (
            <div style={styles.loaderOverlay}>
              <div style={styles.loaderCard}>
                <div style={styles.spinner} />

                <div style={styles.loaderTitle}>Uploading Document</div>

                <div style={styles.loaderSub}>
                  Please wait while the file and metadata are being processed...
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              ...styles.grid,
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2, 1fr)"
                  : "repeat(4, 1fr)",
              ...(isMobile ? styles.gridMobile : null),
            }}
          >
            {/* DOCUMENT TYPE */}

            <div style={styles.field}>
              <label style={styles.label}>Document Type</label>

              <select
                style={fieldInputStyle}
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
              >
                <option value="">Select Document Type</option>

                {documentTypes.map((d) => (
                  <option key={d.lookup_id} value={d.lookup_id}>
                    {d.lookup_name}
                  </option>
                ))}
              </select>
            </div>

            {/* POLICY NUMBER */}

            <div style={styles.field}>
              <label style={styles.label}>Policy Number</label>

              <input
                style={fieldInputStyle}
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="Enter policy number"
              />
            </div>

            {/* LOB */}

            <div style={styles.field}>
              <label style={styles.label}>LOB</label>

              <select
                style={fieldInputStyle}
                value={lob}
                onChange={(e) => setLob(e.target.value)}
              >
                <option value="">Select LOB</option>

                {lobOptions.map((l) => (
                  <option key={l.lookup_id} value={l.lookup_code}>
                    {l.lookup_name}
                  </option>
                ))}
              </select>
            </div>

            {/* AGENCY */}

            <div style={styles.field}>
              <label style={styles.label}>Agency Number</label>

              <input
                style={fieldInputStyle}
                value={agencyNo}
                onChange={(e) => setAgencyNo(e.target.value)}
                placeholder="Enter agency number"
              />
            </div>

            {/* INSURED */}

            <div style={styles.fieldFull}>
              <label style={styles.label}>Primary Insured</label>

              <input
                style={fieldInputStyle}
                value={primaryInsured}
                onChange={(e) => setPrimaryInsured(e.target.value)}
                placeholder="Enter primary insured"
              />
            </div>

            {/* COMMENTS */}

            <div style={styles.fieldFull}>
              <label style={styles.label}>Comments</label>

              <textarea
                style={fieldTextareaStyle}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Enter comments"
              />
            </div>

            {/* FILE */}

            <div style={styles.fieldFull}>
              <label style={styles.label}>Upload File</label>

              <div
                style={{
                  ...styles.fileInputWrapper,
                  ...(isMobile ? styles.fileInputWrapperMobile : null),
                }}
              >
                <input
                  id="manual-upload-input"
                  type="file"
                  multiple
                  accept={ACCEPT_STRING}
                  style={styles.fileInputHidden}
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    setFiles((prev) => {
                      const existingNames = new Set(prev.map((f) => f.name));
                      return [
                        ...prev,
                        ...picked.filter((f) => !existingNames.has(f.name)),
                      ];
                    });
                    /*
                      Reset input value so the same file can be re-added
                      after being removed from the list.
                    */
                    e.target.value = "";
                  }}
                />

                <label
                  htmlFor="manual-upload-input"
                  style={{
                    ...styles.fileInputLabel,
                    ...(isMobile ? styles.fileInputLabelMobile : null),
                  }}
                >
                  <span
                    className="upload-file-choose-btn"
                    style={{
                      ...styles.fileChooseBtn,
                      ...(isMobile ? styles.fileChooseBtnMobile : null),
                    }}
                  >
                    Choose Files
                  </span>
                  <span
                    style={{
                      ...styles.fileStatusText,
                      ...(isMobile ? styles.fileStatusTextMobile : null),
                      color: files.length > 0 ? "#0f172a" : "#94a3b8",
                      fontWeight: files.length > 0 ? 500 : 400,
                    }}
                  >
                    {files.length === 0
                      ? "No file chosen"
                      : files.length === 1
                        ? "1 file selected"
                        : `${files.length} files selected`}
                  </span>
                </label>
              </div>

              {/* SELECTED FILE CHIPS */}

              {files.length > 0 && (
                <div
                  style={{
                    ...styles.chipContainer,
                    ...(isMobile ? styles.chipContainerMobile : null),
                  }}
                >
                  {files.map((f, i) => {
                    const fileErrors = getFileErrors(f);
                    const hasError = fileErrors.length > 0;
                    const isHovered = hoveredChipIndex === i;

                    /*
                      Tooltip content:
                      - Valid file   → full filename (useful when truncated)
                      - Invalid file → each error on its own line + remove hint
                    */
                    const tooltipText = hasError
                      ? fileErrors.join(" · ") + "\nRemove to proceed"
                      : f.name;

                    /*
                      Chip label:
                      - Valid   → file size in KB
                      - Invalid → error(s) joined with " · "
                    */
                    const chipLabel = hasError
                      ? fileErrors.join(" · ")
                      : `(${(f.size / 1024).toFixed(1)} KB)`;

                    return (
                      <span
                        key={f.name}
                        style={{
                          ...styles.chipWrapper,
                          ...(isMobile ? styles.chipWrapperMobile : null),
                        }}
                        onMouseEnter={() => setHoveredChipIndex(i)}
                        onMouseLeave={() => setHoveredChipIndex(null)}
                      >
                        {/* CUSTOM TOOLTIP */}
                        {isHovered && (
                          <span style={hasError ? styles.tooltipOversized : styles.tooltipNormal}>
                            {tooltipText}
                            <span style={hasError ? styles.tooltipArrowOversized : styles.tooltipArrow} />
                          </span>
                        )}

                        <span
                          style={{
                            ...(hasError ? styles.fileChipOversized : styles.fileChip),
                            ...(isMobile ? styles.fileChipMobile : null),
                          }}
                        >
                          <span
                            style={{
                              ...(hasError ? styles.chipNameOversized : styles.chipName),
                              ...(isMobile ? styles.chipNameMobile : null),
                            }}
                          >
                            {f.name}
                          </span>

                          <span
                            style={{
                              ...(hasError ? styles.chipSizeOversized : styles.chipSize),
                              ...(isMobile ? styles.chipSizeMobile : null),
                            }}
                          >
                            {chipLabel}
                          </span>

                          <button
                            type="button"
                            style={hasError ? styles.chipRemoveOversized : styles.chipRemove}
                            title="Remove file"
                            onClick={() =>
                              setFiles((prev) => prev.filter((_, j) => j !== i))
                            }
                          >
                            ×
                          </button>
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ERROR */}

          {error && <div style={styles.error}>{error}</div>}

          {/* ACTIONS */}

          <div style={{ ...styles.footer, ...(isMobile ? styles.footerMobile : null) }}>
            {(() => {
              const hasInvalidFiles = files.some(
                (f) => getFileErrors(f).length > 0,
              );
              const isDisabled = loading || hasInvalidFiles;
              const disabledTitle = hasInvalidFiles
                ? "Remove invalid files (unsupported type or exceeds 50MB) before uploading"
                : undefined;
              return (
                <button
                  style={{
                    ...(isDisabled ? styles.uploadBtnDisabled : styles.uploadBtn),
                    ...(isMobile ? styles.uploadBtnMobile : null),
                  }}
                  onClick={handleUpload}
                  disabled={isDisabled}
                  title={disabledTitle}
                >
                  {loading ? "Uploading..." : "Upload Document"}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {success && (
        <div style={styles.toast} role="status" aria-live="polite">
          <button
            type="button"
            style={styles.toastClose}
            onClick={() => setSuccess(null)}
            aria-label="Dismiss upload confirmation"
          >
            ×
          </button>

          <div style={styles.toastTitle}>
            ✅{" "}
            {success.uploadedFileNames.length === 1
              ? "Upload Successful"
              : `${success.uploadedFileNames.length} Files Uploaded Successfully`}
          </div>

          <div style={styles.toastLabel}>
            Uploaded File{success.uploadedFileNames.length > 1 ? "s" : ""}:
          </div>

          <div style={styles.toastFileRow}>
            {success.uploadedFileNames.map((name) => (
              <span key={name} style={styles.toastFileBadge} title={name}>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   STYLES
========================= */

const styles = {
  container: {
    // padding: "clamp(12px, 4vw, 24px)",
    // background: "#f8fafc",
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    // overflowX: "hidden",
  },

  loaderOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 20,
    borderRadius: "16px",
    backdropFilter: "blur(2px)",
  },

  loaderCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "28px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
  },

  spinner: {
    width: "52px",
    height: "52px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #0f172a",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  loaderTitle: {
    marginTop: "18px",
    fontWeight: "700",
    fontSize: "16px",
    color: "#0f172a",
  },

  loaderSub: {
    marginTop: "8px",
    color: "#64748b",
    fontSize: "13px",
    textAlign: "center",
    maxWidth: "280px",
  },

  card: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    padding: "24px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    width: "100%",
    boxSizing: "border-box",
  },

  cardMobile: {
    padding: "12px",
  },

  header: {
    marginBottom: "24px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "14px",
  },

  formCard: {
    position: "relative",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "clamp(16px, 4vw, 24px)",
    overflow: "hidden",
    boxSizing: "border-box",
    width: "100%",
  },

  formCardMobile: {
    padding: "12px",
    overflow: "visible",
  },

  grid: {
    display: "grid",
    gap: "clamp(12px, 4vw, 18px)",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },

  gridMobile: {
    gap: "12px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },

  fieldFull: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },

  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
  },

  input: {
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
  },

  inputMobile: {
    padding: "10px 12px",
    fontSize: "13px",
  },

  textarea: {
    minHeight: "120px",
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    resize: "vertical",
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
    background: "#fff",
    width: "100%",
    maxWidth: "100%",
    display: "block",
    boxSizing: "border-box",
  },

  textareaMobile: {
    padding: "10px 12px",
    fontSize: "13px",
    minHeight: "100px",
  },

  fileInputWrapper: {
    position: "relative",
    background: "#fff",
    border: "1px dashed #cbd5e1",
    borderRadius: "10px",
    padding: "12px 16px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },

  fileInputWrapperMobile: {
    padding: "10px 12px",
  },

  fileInputHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },

  fileInputLabel: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    margin: 0,
    width: "100%",
    minWidth: 0,
  },

  fileInputLabelMobile: {
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },

  fileChooseBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 18px",
    borderRadius: "8px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: "inherit",
    flexShrink: 0,
    transition: "background 0.15s ease",
  },

  fileChooseBtnMobile: {
    padding: "8px 14px",
    fontSize: "13px",
  },

  fileStatusText: {
    fontSize: "14px",
    fontFamily: "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },

  fileStatusTextMobile: {
    whiteSpace: "normal",
    textAlign: "center",
    width: "100%",
  },

  chipContainer: {
    marginTop: "12px",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },

  chipContainerMobile: {
    flexDirection: "column",
    alignItems: "stretch",
  },

  chipWrapper: {
    position: "relative",
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "100%",
  },

  chipWrapperMobile: {
    display: "flex",
    width: "100%",
    alignItems: "stretch",
    minWidth: 0,
  },

  tooltipNormal: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#1e293b",
    color: "#f8fafc",
    padding: "8px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.5",
    whiteSpace: "normal",
    wordBreak: "break-all",
    maxWidth: "260px",
    minWidth: "120px",
    textAlign: "center",
    boxShadow: "0 6px 20px rgba(15,23,42,0.18)",
    zIndex: 100,
    pointerEvents: "none",
  },

  tooltipOversized: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#7f1d1d",
    color: "#fef2f2",
    padding: "8px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.5",
    whiteSpace: "normal",
    wordBreak: "break-word",
    maxWidth: "260px",
    minWidth: "160px",
    textAlign: "center",
    boxShadow: "0 6px 20px rgba(127,29,29,0.25)",
    zIndex: 100,
    pointerEvents: "none",
  },

  tooltipArrow: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%) rotate(45deg)",
    width: "8px",
    height: "8px",
    background: "#1e293b",
    borderRadius: "1px",
  },

  tooltipArrowOversized: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%) rotate(45deg)",
    width: "8px",
    height: "8px",
    background: "#7f1d1d",
    borderRadius: "1px",
  },

  fileChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: "20px",
    padding: "5px 10px 5px 12px",
    fontSize: "13px",
    color: "#166534",
    maxWidth: "360px",
    minWidth: 0,
    boxSizing: "border-box",
  },

  fileChipMobile: {
    width: "100%",
    maxWidth: "100%",
  },

  fileChipOversized: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    borderRadius: "20px",
    padding: "5px 10px 5px 12px",
    fontSize: "13px",
    color: "#991b1b",
    maxWidth: "360px",
    minWidth: 0,
    boxSizing: "border-box",
  },

  chipName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
    fontWeight: "500",
    color: "#166534",
  },

  chipNameMobile: {
    flex: 1,
    minWidth: 0,
    maxWidth: "none",
  },

  chipNameOversized: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
    fontWeight: "500",
    color: "#991b1b",
  },

  chipSize: {
    color: "#15803d",
    fontSize: "12px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  chipSizeMobile: {
    fontSize: "11px",
  },

  chipSizeOversized: {
    color: "#dc2626",
    fontSize: "12px",
    whiteSpace: "nowrap",
    fontWeight: "600",
    flexShrink: 0,
  },

  chipRemove: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#15803d",
    fontSize: "16px",
    lineHeight: "1",
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    flexShrink: 0,
  },

  chipRemoveOversized: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#dc2626",
    fontSize: "16px",
    lineHeight: "1",
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    flexShrink: 0,
  },

  footer: {
    marginTop: "24px",
    display: "flex",
    justifyContent: "flex-end",
  },

  footerMobile: {
    justifyContent: "center",
  },

  uploadBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
  },

  uploadBtnMobile: {
    padding: "10px 16px",
    fontSize: "13px",
  },

  uploadBtnDisabled: {
    background: "#e2e8f0",
    color: "#94a3b8",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "not-allowed",
    fontWeight: "600",
  },

  error: {
    marginTop: "20px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
  },

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    background: "#16a34a",
    color: "#fff",
    padding: "16px 40px 16px 18px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 9999,
    maxWidth: "420px",
    lineHeight: "1.5",
  },

  toastClose: {
    position: "absolute",
    top: "8px",
    right: "10px",
    border: "none",
    background: "transparent",
    color: "#fff",
    fontSize: "18px",
    lineHeight: 1,
    cursor: "pointer",
    padding: "4px",
    opacity: 0.85,
  },

  toastTitle: {
    fontWeight: "700",
    paddingRight: "8px",
  },

  toastLabel: {
    marginTop: "8px",
    fontSize: "13px",
    fontWeight: "600",
    opacity: 0.95,
  },

  toastFileRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "8px",
  },

  toastFileBadge: {
    background: "#ecfdf5",
    color: "#166534",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
